import { Field, Label, Textarea } from '@headlessui/react';
import { useId, useState } from 'react';

import { api } from '../api/index.ts';
import { ApiError, type Entry, type EntryMessage } from '../api/types.ts';
import { useT } from '../i18n/index.ts';
import { messageTime } from '../lib/dates.ts';
import { Icon } from '../ui/icons.tsx';

const MAX_REPLY = 1000;

/** The owner ↔ office boy conversation on one entry, oldest first. Nothing is ever replaced. */
export function MessageList({ messages }: { messages: EntryMessage[] }) {
  const { t, lang } = useT();
  if (messages.length === 0) return null;
  return (
    <section aria-label={t('history.messages')}>
      <h3 className="m-0 mb-2 text-[13px] font-bold tracking-wide text-muted uppercase rtl:text-[15px] rtl:normal-case">
        {t('history.messages')}
      </h3>
      <ol className="m-0 flex list-none flex-col gap-2 p-0">
        {messages.map((m) => {
          const mine = m.kind === 'reply';
          const tag = m.kind === 'needs_fix' ? t('history.ownerNeedsFix') : m.kind === 'rejected' ? t('history.ownerRejected') : null;
          return (
            <li key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[88%] rounded-2xl border-[1.5px] px-3.5 py-2.5 ${
                  mine
                    ? 'rounded-ee-md border-brand/30 bg-brand-tint'
                    : m.kind === 'rejected'
                      ? 'rounded-es-md border-danger/40 bg-paper'
                      : 'rounded-es-md border-warning/40 bg-[#FFFBEB]'
                }`}
              >
                <div className="flex flex-wrap items-baseline gap-x-2 text-[12px] text-muted rtl:text-[14px]">
                  <span className="font-bold text-ink">{mine ? t('history.you') : (m.authorName ?? t('history.owner'))}</span>
                  {tag && <span className={`font-bold ${m.kind === 'rejected' ? 'text-danger-text' : 'text-warning-text'}`}>{tag}</span>}
                  {m.createdAt && (
                    <time dateTime={m.createdAt} className="num">
                      {messageTime(m.createdAt, lang)}
                    </time>
                  )}
                </div>
                <p className="m-0 mt-0.5 text-[15px] break-words whitespace-pre-wrap rtl:text-[17px]">{m.body}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/** Reply box under a "Needs fix" entry: the answer goes to the owner and the entry back to Pending. */
export function ReplyBox({ entry, onSent }: { entry: Entry; onSent: () => void | Promise<void> }) {
  const { t } = useT();
  const hintId = useId();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    const message = text.trim();
    if (message.length < 2) {
      setError(t('history.replyShort'));
      return;
    }
    if (!navigator.onLine) {
      setError(t('history.replyOffline'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.replyToFix(entry.id, entry.entityId, message);
      setText('');
      await onSent();
    } catch (e) {
      setError(e instanceof ApiError && e.status === 0 ? t('history.replyOffline') : t('history.replyFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void send();
      }}
      className="flex flex-col gap-2"
    >
      <Field className="flex flex-col gap-1.5">
        <Label className="text-[14px] font-bold rtl:text-[16px]">{t('history.replyLabel')}</Label>
        <Textarea
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (error) setError(null);
          }}
          rows={3}
          maxLength={MAX_REPLY}
          placeholder={t('history.replyHint')}
          aria-describedby={error ? hintId : undefined}
          aria-invalid={error ? true : undefined}
          className="min-h-[88px] w-full resize-y rounded-xl border-[1.5px] border-line-strong bg-paper px-3.5 py-3 text-[16px] text-ink placeholder:text-muted focus:border-ink focus:outline-none data-focus:outline-3 data-focus:outline-offset-1 data-focus:outline-ink/20 rtl:text-[17px]"
        />
      </Field>
      {error && (
        <p id={hintId} role="alert" className="m-0 text-[13px] font-bold text-danger-text rtl:text-[15px]">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={busy}
        className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-brand px-4 text-[16px] font-bold text-white hover:bg-brand-dark disabled:opacity-60 focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ink"
      >
        <Icon name="send" size={18} />
        {busy ? t('history.sending') : t('history.sendReply')}
      </button>
    </form>
  );
}
